import React, { useEffect, useState } from 'react';
import { Alert, Button, Modal, Radio, Select, Steps, Table, message } from 'antd';
import { bulkPaidReports, previewBulkPaid } from '../api/ownerReporting';
import styles from './BulkAuditWizard.module.css';
import { monthLabel, packageTypeLabel } from './BulkAuditWizard';

const BulkPaidWizard = ({
  visible,
  hostId,
  properties,
  propertiesLoading,
  propertyId,
  year,
  month,
  onClose,
  onSuccess,
}) => {
  const [step, setStep] = useState(0);
  const [selectedPropertyId, setSelectedPropertyId] = useState(propertyId);
  const [packageType, setPackageType] = useState(undefined);
  const [preview, setPreview] = useState({ toPay: [], alreadyPaid: [], missingReport: [] });
  const [previewLoading, setPreviewLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const reset = () => {
    setStep(0);
    setSelectedPropertyId(propertyId);
    setPackageType(undefined);
    setPreview({ toPay: [], alreadyPaid: [], missingReport: [] });
    setPreviewLoading(false);
    setSaving(false);
  };

  useEffect(() => {
    if (!visible) return undefined;
    setSelectedPropertyId(propertyId);
    setStep(0);
    setPackageType(undefined);
    setPreview({ toPay: [], alreadyPaid: [], missingReport: [] });
    return undefined;
  }, [visible, propertyId]);

  const handleClose = () => {
    reset();
    if (onClose) onClose();
  };

  const loadPreview = () => {
    if (!hostId || !selectedPropertyId || !packageType) return;
    setPreviewLoading(true);
    previewBulkPaid(hostId, {
      propertyId: selectedPropertyId,
      packageType,
      year,
      month,
    })
      .then(data => {
        setPreview({
          toPay: (data && data.toPay) || [],
          alreadyPaid: (data && data.alreadyPaid) || [],
          missingReport: (data && data.missingReport) || [],
        });
      })
      .catch(() => {
        message.error('Failed to load bulk paid preview');
        setPreview({ toPay: [], alreadyPaid: [], missingReport: [] });
      })
      .then(() => {
        setPreviewLoading(false);
      });
  };

  const goNext = () => {
    if (step === 0 && !selectedPropertyId) return;
    if (step === 1 && !packageType) return;
    const next = step + 1;
    setStep(next);
    if (next === 2) loadPreview();
  };

  const goBack = () => setStep(Math.max(0, step - 1));

  const onConfirm = async () => {
    if (!hostId || !selectedPropertyId || !packageType) return;
    if (!(preview.toPay || []).length) return;
    setSaving(true);
    try {
      const result = await bulkPaidReports(hostId, {
        propertyId: selectedPropertyId,
        packageType,
        year,
        month,
      });
      const paidCount = result && result.paidCount != null ? result.paidCount : (preview.toPay || []).length;
      const skippedPaid = result && result.skippedPaidCount ? result.skippedPaidCount : 0;
      if (skippedPaid) {
        message.success(`Marked ${paidCount} paid. ${skippedPaid} already paid were skipped.`);
      } else {
        message.success(`Marked ${paidCount} paid`);
      }
      reset();
      if (onSuccess) onSuccess();
    } catch (e) {
      message.error('Bulk paid failed');
      setSaving(false);
    }
  };

  const toPay = preview.toPay || [];
  const alreadyPaid = preview.alreadyPaid || [];
  const missingReport = preview.missingReport || [];
  const selectedProperty = (properties || []).find(p => String(p._id) === String(selectedPropertyId));
  const propertyName = (selectedProperty && selectedProperty.name) || '';

  const footer = [
    <Button key="cancel" onClick={handleClose}>
      Cancel
    </Button>,
  ];
  if (step > 0) {
    footer.push(
      <Button key="back" onClick={goBack}>
        Back
      </Button>
    );
  }
  if (step < 2) {
    footer.push(
      <Button
        key="next"
        type="primary"
        disabled={(step === 0 && !selectedPropertyId) || (step === 1 && !packageType)}
        onClick={goNext}
      >
        Next
      </Button>
    );
  } else {
    footer.push(
      <Button key="confirm" type="primary" loading={saving || previewLoading} disabled={!toPay.length} onClick={onConfirm}>
        Mark {toPay.length} paid
      </Button>
    );
  }

  return (
    <Modal
      visible={!!visible}
      title="Bulk Paid"
      onCancel={handleClose}
      footer={footer}
      width={720}
      wrapClassName={styles.wrap}
      style={{ top: 24 }}
      destroyOnClose
    >
      <Steps current={step} size="small" direction="horizontal" className={styles.steps}>
        <Steps.Step title="Property" />
        <Steps.Step title="Package type" />
        <Steps.Step title="Review" />
      </Steps>
      {step === 0 && (
        <div>
          <div className={styles.hint}>
            Month {monthLabel(year, month)}. Change month on Manage Reporting first if this is the wrong period.
          </div>
          <div className={styles.fieldLabel}>Property</div>
          <Select
            showSearch
            optionFilterProp="children"
            style={{ width: '100%' }}
            placeholder={propertiesLoading ? 'Loading properties...' : 'Select property'}
            value={selectedPropertyId}
            onChange={val => setSelectedPropertyId(val)}
            loading={propertiesLoading}
          >
            {(properties || []).map(p => (
              <Select.Option key={String(p._id)} value={p._id}>
                {p.name}
              </Select.Option>
            ))}
          </Select>
        </div>
      )}
      {step === 1 && (
        <div>
          <div className={styles.hint}>
            {propertyName || 'Property'} · {monthLabel(year, month)}. All unpaid units in packages of this type at the
            property will be listed next.
          </div>
          <Radio.Group className={styles.radios} value={packageType} onChange={e => setPackageType(e.target.value)}>
            <Radio value="profit-sharing">Profit Sharing</Radio>
            <Radio value="tenancy-agreement">Fixed Rental</Radio>
          </Radio.Group>
        </div>
      )}
      {step === 2 && (
        <div>
          <div className={styles.summary}>
            <strong>{propertyName || 'Property'}</strong>
            {' · '}
            {packageTypeLabel(packageType)}
            {' · '}
            {monthLabel(year, month)}
          </div>
          {alreadyPaid.length > 0 && (
            <Alert
              type="info"
              showIcon
              style={{ marginBottom: 12 }}
              message={`${alreadyPaid.length} unit${alreadyPaid.length === 1 ? '' : 's'} already paid (skipped)`}
              description={alreadyPaid.map(r => r.unitName || String(r.unitId)).join(', ')}
            />
          )}
          {missingReport.length > 0 && (
            <Alert
              type="warning"
              showIcon
              style={{ marginBottom: 12 }}
              message={`${missingReport.length} unit${missingReport.length === 1 ? '' : 's'} have no report yet (skipped)`}
              description={missingReport.map(r => r.unitName || String(r.unitId)).join(', ')}
            />
          )}
          <div className={styles.tableWrap}>
            <Table
              size="small"
              rowKey={r => String(r.unitId)}
              loading={previewLoading}
              dataSource={toPay}
              pagination={false}
              scroll={{ x: 480 }}
              locale={{ emptyText: 'No unpaid reports for this property and package type' }}
              columns={[
                {
                  title: 'Unit',
                  key: 'unit',
                  render: r => r.unitName || String(r.unitId),
                },
                {
                  title: 'Service Package',
                  key: 'package',
                  dataIndex: 'packageName',
                },
                {
                  title: 'Total Payout',
                  key: 'payout',
                  dataIndex: 'amountTransferToOwner',
                  align: 'right',
                },
                {
                  title: 'Audit',
                  key: 'audit',
                  dataIndex: 'auditStatus',
                },
              ]}
            />
          </div>
        </div>
      )}
    </Modal>
  );
};

export default BulkPaidWizard;
